import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

// import { ShellComponent } from '@core/shell/shell.component';
import { AuthGuard } from '@platform/auth/auth.guard';
import { createAuthorizeRouteData } from '@platform/auth/authorize.model';
import { combineData, createShowProgressBar } from '@platform/routing/params-helper';

import { MobileUploadGuard } from '@core/mobile-upload/mobile-upload.guard';
import { MobileUploadExpiredComponent } from '@core/mobile-upload/mobile-upload-expired/mobile-upload-expired.component';
import { HomeShellComponent } from '@core/home/home-shell/home-shell.component';
import { TasksOverviewComponent } from '@apps/fiduciary/tasks/tasks-overview/tasks-overview.component';
import { homeStoreCurrentRouteGuard } from '@core/home/home-store-current-route.guard';
import { homeRedirectToStoredRouteGuard } from '@core/home/home-redirect-to-stored-route.guard';
import { ProjectListComponent } from '@apps/projects/project-list/project-list.component';
import { AdminGuard } from './admin/admin.guard';
import { AdminComponent } from './admin/admin.component';
import { UserManagementComponent } from './user-management/user-management.component';
import { UserManagementCompositeGuard } from './user-management/user-management-composite.guard';
import { CallbackComponent } from './callback/callback.component';
import { CockpitCompositeGuard } from './cockpit/cockpit-composite.guard';
import { ContactsCompositeGuard } from './contacts/contacts-composite.guard';
import { ContactsComponent } from './contacts/contacts.component';
import { CustomerSpaceAdminCompositeGuard } from './customer-space-admin/customer-space-admin-composite.guard';
import { CustomerSpaceAdminComponent } from './customer-space-admin/customer-space-admin.component';
import { CustomerSpaceListComponent } from './customer-space-list/customer-space-list.component';
import { CustomerSpaceListGuard } from './customer-space-list/customer-space-list.guard';
import { CustomerSpaceCompositeGuard } from './customer-space/customer-space-composite.guard';
import { FatalErrorComponent } from './fatal-error/fatal-error.component';
import { LogoutComponent } from './logout/logout.component';
import { NavigationErrorComponent } from './navigation-error/navigation-error.component';
import { NotFoundComponent } from './notfound/notfound.component';
import { UnauthenticatedComponent } from './unauthenticated/unauthenticated.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';
import { UserListCompositeGuard } from './user-list/user-list-composite.guard';
import { UserListComponent } from './user-list/user-list.component';
import { UserProfileCompositeGuard } from './user-profile/user-profile-composite.guard';
import { MobileUploadComponent } from './mobile-upload/mobile-upload.component';
import { CockpitComponent } from './cockpit/cockpit.component';
import { CreateUserComponent } from './create-user/create-user.component';
import { UserProfileComponent } from './user-profile/user-profile.component';
import { EditUserComponent } from './edit-user/edit-user.component';
import { CustomerSpaceAdminV2Component } from './customer-space-admin-v2/customer-space-admin-v2.component';
import { ShellComponent } from './shell/shell.component';
import { CreateUserCompositeGuard } from './create-user/create-user-composite.guard';

const routes: Routes = [
  { path: 'oauth/callback', component: CallbackComponent, pathMatch: 'full' },
  { path: 'unauthorized', component: UnauthorizedComponent, pathMatch: 'full' },
  { path: 'unauthenticated', component: UnauthenticatedComponent, pathMatch: 'full' },
  { path: 'notfound', component: NotFoundComponent, pathMatch: 'full' },
  { path: 'fatal-error', component: FatalErrorComponent, pathMatch: 'full' },
  { path: 'error', component: NavigationErrorComponent, pathMatch: 'full' },
  { path: 'logout', component: LogoutComponent, pathMatch: 'full' },
  {
    path: 'customer-spaces/:customerSpaceId/mobile-upload/:mobileUploadId',
    component: MobileUploadComponent,
    pathMatch: 'full',
    canActivate: [MobileUploadGuard],
  },
  { path: 'mobile-upload-expired', component: MobileUploadExpiredComponent, pathMatch: 'full' },
  {
    path: 'customer-spaces/create',
    component: ShellComponent,
    canActivate: [AuthGuard],
    data: createShowProgressBar(),
    children: [
      {
        path: '',
        component: CustomerSpaceAdminComponent,
        pathMatch: 'full',
        runGuardsAndResolvers: 'always',
        canActivate: [CustomerSpaceAdminCompositeGuard],
        data: createAuthorizeRouteData(['CUSTOMER_SPACE_CREATE']),
      },
    ],
  },
  {
    path: 'customer-spaces/:customerSpaceId',
    component: ShellComponent,
    runGuardsAndResolvers: 'always',
    canActivate: [CustomerSpaceCompositeGuard],
    children: [
      { path: '', redirectTo: 'cockpit', pathMatch: 'full' },
      {
        path: 'cockpit',
        component: CockpitComponent,
        pathMatch: 'full',
        canActivate: [CockpitCompositeGuard],
        data: createAuthorizeRouteData(['CUSTOMER_SPACE_READ', 'COCKPIT_READ']),
      },
      {
        path: 'contacts',
        component: ContactsComponent,
        pathMatch: 'full',
        canActivate: [ContactsCompositeGuard],
        data: createAuthorizeRouteData(['CUSTOMER_SPACE_READ']),
      },
      // {
      //   path: 'admin-v1',
      //   component: CustomerSpaceAdminComponent,
      //   pathMatch: 'full',
      //   canActivate: [CustomerSpaceAdminCompositeGuard],
      //   data: createAuthorizeRouteData(['CUSTOMER_SPACE_WRITE']),
      // },
      {
        path: 'admin',
        component: CustomerSpaceAdminV2Component,
        pathMatch: 'full',
        canActivate: [CustomerSpaceAdminCompositeGuard],
        data: createAuthorizeRouteData(['CUSTOMER_SPACE_WRITE']),
      },
      {
        path: 'admin/users/create',
        component: CreateUserComponent,
        pathMatch: 'full',
        canActivate: [CreateUserCompositeGuard],
        data: createAuthorizeRouteData(['USERS_WRITE']),
      },
    ],
    data: combineData(createAuthorizeRouteData(['CUSTOMER_SPACE_READ']), createShowProgressBar()),
  },
  {
    path: 'users',
    component: ShellComponent,
    canActivate: [AuthGuard],
    data: createShowProgressBar(),
    children: [
      {
        path: '',
        component: UserManagementComponent,
        pathMatch: 'full',
        canActivate: [UserManagementCompositeGuard],
        data: createAuthorizeRouteData(['USERS_WRITE']),
      },
    ],
  },
  {
    path: 'users-v1',
    component: ShellComponent,
    canActivate: [AuthGuard],
    data: createShowProgressBar(),
    children: [
      {
        path: '',
        component: UserListComponent,
        pathMatch: 'full',
        canActivate: [UserListCompositeGuard],
        data: createAuthorizeRouteData(['USERS_WRITE']),
      },
    ],
  },
  // {
  //   path: 'users/create-v1',
  //   component: ShellComponent,
  //   canActivate: [AuthGuard],
  //   data: createShowProgressBar(),
  //   children: [
  //     {
  //       path: '',
  //       component: CreateUserComponent,
  //       pathMatch: 'full',
  //       canActivate: [CreateUserCompositeGuard],
  //       data: createAuthorizeRouteData(['USERS_WRITE']),
  //     },
  //   ],
  // },
  {
    path: 'user',
    component: ShellComponent,
    canActivate: [AuthGuard],
    data: createShowProgressBar(),
    children: [
      {
        path: '',
        component: UserProfileComponent,
        pathMatch: 'full',
        canActivate: [UserProfileCompositeGuard],
      },
    ],
  },
  {
    path: 'users/:userId',
    component: ShellComponent,
    canActivate: [AuthGuard],
    data: createShowProgressBar(),
    children: [
      {
        path: '',
        component: EditUserComponent,
        pathMatch: 'full',
        canActivate: [UserProfileCompositeGuard],
      },
    ],
  },
  {
    path: 'user-profile',
    component: ShellComponent,
    canActivate: [AuthGuard],
    data: createShowProgressBar(),
    children: [
      {
        path: '',
        component: UserProfileComponent,
        pathMatch: 'full',
        canActivate: [UserProfileCompositeGuard],
      },
    ],
  },
  {
    path: 'admin',
    component: ShellComponent,
    canActivate: [AuthGuard],
    data: createShowProgressBar(),
    children: [
      {
        path: '',
        component: AdminComponent,
        pathMatch: 'full',
        canActivate: [AdminGuard],
        data: createAuthorizeRouteData(['PORTAL_ADMIN']),
      },
    ],
  },
  // {
  //   path: 'admin-v1',
  //   component: ShellComponent,
  //   canActivate: [AuthGuard],
  //   data: createShowProgressBar(),
  //   children: [
  //     {
  //       path: '',
  //       component: AdminComponent,
  //       pathMatch: 'full',
  //       canActivate: [AdminGuard],
  //       data: createAuthorizeRouteData(['PORTAL_ADMIN']),
  //     },
  //   ],
  // },
  {
    path: 'home',
    component: HomeShellComponent,
    canActivate: [AuthGuard],
    data: createShowProgressBar(),
    children: [
      // guard used here because angular does not support dynamic redirects yet
      // see https://github.com/angular/angular/issues/13373
      { path: '', canActivate: [homeRedirectToStoredRouteGuard], pathMatch: 'full', children: [] },
      {
        path: 'customer-spaces',
        component: CustomerSpaceListComponent,
        pathMatch: 'full',
        canActivate: [homeStoreCurrentRouteGuard, CustomerSpaceListGuard],
      },
      // {
      //   path: 'projects-v1',
      //   component: ProjectListComponent,
      //   pathMatch: 'full',
      //   canActivate: [homeStoreCurrentRouteGuard, ProjectListGuard],
      // },
      {
        path: 'projects',
        component: ProjectListComponent,
        pathMatch: 'full',
        canActivate: [homeStoreCurrentRouteGuard],
      },
      {
        path: 'tasks',
        component: TasksOverviewComponent,
        pathMatch: 'full',
        canActivate: [homeStoreCurrentRouteGuard],
      },
    ],
  },
  {
    path: '**',
    component: NotFoundComponent,
    pathMatch: 'full',
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class CoreRoutingModule {}
